import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useRelay } from '../../../hooks/useRelay';
import { applyAction, parseSGFContent } from '../../../games/baduk-analysis/baduk';
import type { BadukState, BadukAction, AnnotationType } from '../../../games/baduk-analysis/baduk';
import type { RoomView } from '../../../types/room';
import BadukAnalysis from './BadukAnalysis';
import type { AIAnalysisResult, AIAnalysisStatus, SkillLevelOption, AIMoveResult } from './types/baduk.types';
import { useSocket } from '../../../contexts/SocketContext';

interface Props {
  slug: string;
  room: RoomView;
  myId: string;
  isLeader: boolean;
  initialState: BadukState | null;
  onFinish: () => void;
}

export default function BadukRoomGame({ slug, room, myId, isLeader, initialState, onFinish }: Props) {
  const { state, startRelay, sendAction } = useRelay<BadukState>(slug);
  const relayStartedRef = useRef(false);
  const { socket } = useSocket();

  // AI analysis state (KataGo — handled via direct socket events, not relay)
  const [analysisResult, setAnalysisResult] = useState<AIAnalysisResult | null>(null);
  const [analysisStatus, setAnalysisStatus] = useState<AIAnalysisStatus | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [skillLevels, setSkillLevels] = useState<SkillLevelOption[]>([]);
  const [lastAIMove, setLastAIMove] = useState<AIMoveResult | null>(null);

  // Leader: start relay on mount
  useEffect(() => {
    if (!isLeader || !initialState || relayStartedRef.current) return;
    relayStartedRef.current = true;
    startRelay('baduk-analysis', initialState);
  }, [isLeader, initialState, startRelay]);

  // KataGo AI: when leader, request AI move if enabled and it's AI's turn
  useEffect(() => {
    if (!isLeader || !state || !socket) return;
    const { aiOpponent } = state;
    if (!aiOpponent.enabled || aiOpponent.isThinking || state.phase !== 'analyzing') return;
    if (state.currentTurn !== aiOpponent.color) return;
    socket.emit('baduk:request-katago-move-with-board', {
      slug,
      board: state.board,
      currentTurn: state.currentTurn,
      komi: state.metadata.komi,
      skillLevel: aiOpponent.skillLevel,
    });
  }, [state, isLeader, socket, slug]);

  // Listen for KataGo responses (AI move + analysis)
  useEffect(() => {
    if (!socket) return;
    const handleAIMove = (data: { move: { x: number; y: number } | null; isPass: boolean }) => {
      if (!isLeader || !state) return;
      let action: BadukAction;
      if (data.isPass || !data.move) {
        action = { type: 'pass', playerId: 'ai' };
      } else {
        action = { type: 'placeStone', playerId: 'ai', x: data.move.x, y: data.move.y };
      }
      // Clear thinking state first, then apply move
      const withThinking = applyAction(state, { type: 'setAIThinking', isThinking: false });
      const next = applyAction(withThinking, action);
      sendAction(action, next);
    };
    const handleThinking = () => {
      if (!isLeader || !state) return;
      const next = applyAction(state, { type: 'setAIThinking', isThinking: true });
      sendAction({ type: 'setAIThinking', isThinking: true }, next);
    };
    const handleAnalysisResult = (data: AIAnalysisResult) => { setAnalysisResult(data); setIsAnalyzing(false); setAnalysisStatus(null); };
    const handleAnalysisStatus = (data: AIAnalysisStatus) => setAnalysisStatus(data);
    const handleSkillLevels = (data: SkillLevelOption[]) => setSkillLevels(data);
    socket.on('baduk:katago-move', handleAIMove);
    socket.on('baduk:katago-thinking', handleThinking);
    socket.on('baduk:analysis-result', handleAnalysisResult);
    socket.on('baduk:analysis-status', handleAnalysisStatus);
    socket.on('baduk:skill-levels', handleSkillLevels);
    return () => {
      socket.off('baduk:katago-move', handleAIMove);
      socket.off('baduk:katago-thinking', handleThinking);
      socket.off('baduk:analysis-result', handleAnalysisResult);
      socket.off('baduk:analysis-status', handleAnalysisStatus);
      socket.off('baduk:skill-levels', handleSkillLevels);
    };
  }, [socket, isLeader, state, sendAction]);

  if (!state) {
    return <div style={{ padding: 40, fontFamily: 'sans-serif', color: '#888', textAlign: 'center' }}>Loading game...</div>;
  }

  const send = (action: BadukAction) => sendAction(action, applyAction(state, action));

  // Adapter: bridge BadukAnalysis component's socket-based actions to relay actions
  const actions = {
    placeStone: (x: number, y: number) => send({ type: 'placeStone', playerId: myId, x, y }),
    pass: () => send({ type: 'pass', playerId: myId }),
    navigate: (direction: string, nodeId?: string) => send({ type: 'navigate', direction: direction as 'back' | 'forward' | 'to-node' | 'to-start' | 'to-end', nodeId }),
    navigateToNode: (nodeId: string) => send({ type: 'navigate', direction: 'to-node', nodeId }),
    goBack: () => send({ type: 'navigate', direction: 'back' }),
    goForward: () => send({ type: 'navigate', direction: 'forward' }),
    goToStart: () => send({ type: 'navigate', direction: 'to-start' }),
    goToEnd: () => send({ type: 'navigate', direction: 'to-end' }),
    uploadSGF: (sgfContent: string) => {
      try {
        const data = parseSGFContent(sgfContent);
        send({ type: 'loadSGF', data });
      } catch { /* ignore parse errors */ }
    },
    setComment: (comment: string) => send({ type: 'setComment', comment }),
    addAnnotation: (type: string, x: number, y: number, label?: string) => send({ type: 'addAnnotation', annotationType: type as AnnotationType, x, y, label }),
    removeAnnotation: (x: number, y: number) => send({ type: 'removeAnnotation', x, y }),
    deleteVariation: (nodeId: string) => send({ type: 'deleteVariation', nodeId }),
    reset: (keepMetadata: boolean = false) => send({ type: 'reset', keepMetadata }),
    toggleDeadStone: (x: number, y: number) => send({ type: 'toggleDeadStone', x, y }),
    acceptScore: () => send({ type: 'acceptScore' }),
    resumeGame: () => send({ type: 'resumeGame' }),
    configureAI: (settings: Partial<BadukState['aiOpponent']>) => {
      send({ type: 'configureAI', settings });
      if (settings.enabled && socket) socket.emit('baduk:configure-katago', { slug, ...settings });
    },
    requestAnalysis: (options?: { maxVisits?: number }) => {
      setIsAnalyzing(true);
      setAnalysisStatus({ status: 'starting', message: 'Requesting analysis...' });
      if (socket && state) socket.emit('baduk:request-analysis', { slug, board: state.board, currentTurn: state.currentTurn, komi: state.metadata.komi, options });
    },
    clearAnalysis: () => { setAnalysisResult(null); setAnalysisStatus(null); setIsAnalyzing(false); },
    requestSkillLevels: () => { if (socket) socket.emit('baduk:get-skill-levels'); },
    requestAIMove: () => { if (socket) socket.emit('baduk:request-katago-move', { slug }); },
    requestState: () => {},  // handled by useRelay
  };

  const myPlayer = room.participants.find(p => p.id === myId);

  return (
    <div style={{ minHeight: '100vh', background: '#1a1a2e', color: '#eee' }}>
      <BadukAnalysis
        socket={null}
        slug={slug}
        playerName={myPlayer?.name ?? ''}
        isLeader={isLeader}
        gameState={state}
        actionsOverride={actions}
        analysisResult={analysisResult}
        analysisStatus={analysisStatus}
        isAnalyzing={isAnalyzing}
        skillLevels={skillLevels}
        lastAIMove={lastAIMove}
      />
      {isLeader && state.phase === 'finished' && (
        <div style={{ textAlign: 'center', paddingBottom: 24 }}>
          <button onClick={onFinish} style={{ padding: '8px 24px' }}>Play Again</button>
        </div>
      )}
    </div>
  );
}
